import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { NotificationSoundContext } from '../App'; // Assuming App.js exports this context
import { useAuth } from '../contexts/AuthContext';

// Create a shared state for all hook instances
const sharedState = {
  pollingActive: false,
  lastFetchTime: 0,
  currentPollingInstance: null,
  minimumPollingInterval: 5000, // 5 seconds minimum between polls
  subscribedComponents: new Set()
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false); // State for panel visibility
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingError, setFetchingError] = useState(null);
  const notificationAudioRef = useRef(null);
  const lastNotificationIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const notificationPanelRef = useRef(null); // Ref for the panel itself
  const componentIdRef = useRef(`notification-component-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`);
  const navigate = useNavigate();
  const { playNotificationSound } = useContext(NotificationSoundContext);
  const { user } = useAuth();

  // Debug function to track state changes
  const debugLog = (message, data) => {
    // Only log in development mode and when explicitly enabled
    const isDebugMode = true; // Enable debugging for now to diagnose notification issues
    const isDev = process.env.NODE_ENV === 'development' || true; // Always log for now
    
    if (isDev && isDebugMode) {
      console.log(`[Notifications Debug] ${message}`, data);
    }
  };

  // Preload notification sound
  useEffect(() => {
    if (!notificationAudioRef.current) {
      notificationAudioRef.current = new Audio('/MessageNotification.mp3');
      notificationAudioRef.current.load();
      debugLog('Preloaded notification sound');
    }
    return () => {
      if (notificationAudioRef.current) {
        notificationAudioRef.current.pause();
        notificationAudioRef.current = null;
      }
    };
  }, []);

  // Handle clicks outside the notification panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the panel ref
      if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
        // We also need to ensure the click wasn't on the bell icon itself (handled separately)
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Mark notifications as read in the backend
  const markAsRead = useCallback(async (notificationIds) => {
    if (!notificationIds.length) return;
    
    try {
      // For each notification, call the API to mark it as read
      const promises = notificationIds.map(id => 
        axiosInstance.post(`/notifications/${id}/mark-read`)
      );
      
      await Promise.all(promises);
      debugLog('Marked notifications as read in backend', { count: notificationIds.length });
      
      // Update the local state to reflect read status
      setNotifications(prev => 
        prev.map(n => 
          notificationIds.includes(n._id) ? { ...n, read: true } : n
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - notificationIds.length));
      
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  }, []);

  // Handle when notifications panel is opened - mark all as read
  useEffect(() => {
    if (showNotifications && notifications.length > 0) {
      const unreadIds = notifications
        .filter(n => !n.read)
        .map(n => n._id);
      
      if (unreadIds.length > 0) {
        markAsRead(unreadIds);
      }
    }
  }, [showNotifications, notifications, markAsRead]);

  // Fetch notifications with retry logic - need to declare this variable before using it
  const fetchNotificationsWithRetry = useCallback(async (silent = false, retryCount = 0) => {
    if (!user) {
      debugLog('No user, skipping fetch');
      return;
    }
    
    // Check if we're within the minimum polling interval
    const now = Date.now();
    const timeSinceLastFetch = now - sharedState.lastFetchTime;
    if (timeSinceLastFetch < sharedState.minimumPollingInterval) {
      debugLog('Skipping fetch, too soon since last fetch', { 
        timeSinceLastFetch,
        minimumInterval: sharedState.minimumPollingInterval 
      });
      return;
    }
    
    // Update last fetch time
    sharedState.lastFetchTime = now;
    
    // Maximum retries
    const MAX_RETRIES = 3;
    
    if (!silent) {
      setLoading(true);
    }
    
    setFetchingError(null);
    
    try {
      debugLog('Fetching notifications', { silent, retryCount });
      
      const response = await axiosInstance.get('/notifications/recent');
      const fetchedNotifications = response.data || [];
      
      console.log('Fetched notifications:', fetchedNotifications);
      
      // Process notifications...
      if (fetchedNotifications.length > 0) {
        const newestNotificationId = fetchedNotifications[0]._id;
        
        // Get current notifications without triggering a state update
        const currentNotifications = notifications;
        
        // Check if there are actually new notifications compared to the current state
        // and identify which ones are unread
        const allNewNotifications = fetchedNotifications.filter(fn => 
          !currentNotifications.some(cn => cn._id === fn._id)
        );
        
        const newUnreadNotifications = allNewNotifications.filter(n => !n.read);
        
        if (newUnreadNotifications.length > 0) {
          debugLog('New unread notifications detected', newUnreadNotifications.map(n => n._id));
          
          // Only play sound for new unread notifications
          playNotificationSound();
          
          // Show desktop notification only for the newest unread SMS notification
          const newestUnreadNotification = newUnreadNotifications[0];
          if (newestUnreadNotification.type === 'sms') {
            showDesktopNotification(newestUnreadNotification);
          }
          
          // Update unread count incrementally IF the panel is closed
          if (!showNotifications) {
            setUnreadCount(prev => prev + newUnreadNotifications.length);
          }
        }
        
        // Update the last seen ID
        lastNotificationIdRef.current = newestNotificationId;
      }
      
      // Always update the notifications state with all fetched notifications
      setNotifications(fetchedNotifications);
      
      // Recalculate total unread count from the full fetched list
      const totalUnread = fetchedNotifications.filter(n => !n.read).length;
      setUnreadCount(totalUnread);
      
      debugLog('Fetch completed', { 
        count: fetchedNotifications.length, 
        unreadCount: totalUnread
      });
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
      
      // Implement retry logic for network errors
      if (retryCount < MAX_RETRIES && (error.code === 'ECONNABORTED' || error.message.includes('Network Error'))) {
        debugLog(`Retrying notification fetch (${retryCount + 1}/${MAX_RETRIES})...`);
        
        // Exponential backoff: wait longer between each retry
        const backoffTime = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          fetchNotificationsWithRetry(silent, retryCount + 1);
        }, backoffTime);
        
        return;
      }
      
      setFetchingError(error.message || 'Failed to fetch notifications');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user, notifications, showNotifications, playNotificationSound]);

  // Simple wrapper to maintain backward compatibility
  const fetchNotifications = useCallback((silent = false) => {
    return fetchNotificationsWithRetry(silent, 0);
  }, [fetchNotificationsWithRetry]);

  // Handler for clicking on a notification
  const handleNotificationClick = useCallback((notification) => {
    debugLog('Notification clicked:', notification);
    
    // Mark as read if not already
    if (!notification.read) {
      markAsRead([notification._id]);
    }
    
    // Handle navigation or other actions based on notification type
    if (notification.type === 'sms' && notification.customerId) {
      // Navigate to customer modal with SMS tab active
      // Use customerId parameter to ensure the modal opens with the messages tab active
      const url = `/customers?tab=manage&customerId=${notification.customerId}&activeTab=messages`;
      debugLog('Navigating to URL:', url);
      navigate(url);
      setShowNotifications(false);
    } else {
      // Default action for other notification types
      setShowNotifications(false);
    }
  }, [navigate, markAsRead, debugLog]);

  // Handler for marking all notifications as read
  const handleMarkAllRead = useCallback(async () => {
    try {
      setLoading(true);
      
      // Call the backend API to mark all as read
      await axiosInstance.post('/notifications/mark-read');
      
      // Update the local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      
      debugLog('Marked all notifications as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Start polling when user is available
  useEffect(() => {
    const componentId = componentIdRef.current;
    debugLog('Component mounted', { componentId });
    
    // Register this component instance
    sharedState.subscribedComponents.add(componentId);
    
    // If this is the first instance or the current polling instance is gone, take over polling
    const shouldHandlePolling = !sharedState.pollingActive || 
                               !sharedState.currentPollingInstance || 
                               !sharedState.subscribedComponents.has(sharedState.currentPollingInstance);
    
    if (shouldHandlePolling && user) {
      debugLog('Taking over notification polling', { componentId });
      
      // Set this instance as the active poller
      sharedState.pollingActive = true;
      sharedState.currentPollingInstance = componentId;
      
      // Fetch immediately on mount/user change
      fetchNotifications();

      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Start new polling interval - 10 seconds is a reasonable interval
      pollingIntervalRef.current = setInterval(() => {
        fetchNotifications(true); // silent refresh
      }, 10000); // Poll every 10 seconds
      
      debugLog('Polling interval started', { componentId, interval: 10000 });
    } else {
      debugLog('Not handling polling, already active or no user', { 
        shouldHandle: shouldHandlePolling,
        hasUser: !!user,
        currentPoller: sharedState.currentPollingInstance 
      });
      
      // If user is not available (logged out), clear notifications state
      if (!user) {
        setNotifications([]);
        setUnreadCount(0);
        lastNotificationIdRef.current = null;
        
        // Also clean up interval if it exists
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }

    // Cleanup function to clear interval on unmount or user change
    return () => {
      debugLog('Component unmounting', { componentId });
      
      // Unregister this component instance
      sharedState.subscribedComponents.delete(componentId);
      
      // If this was the active polling instance, clean up and allow another to take over
      if (sharedState.currentPollingInstance === componentId) {
        debugLog('Cleaning up polling as active instance', { componentId });
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        sharedState.pollingActive = false;
        sharedState.currentPollingInstance = null;
        
        // If there are other components, let the first one take over
        if (sharedState.subscribedComponents.size > 0) {
          debugLog('Other components exist, one will take over', {
            componentCount: sharedState.subscribedComponents.size
          });
        }
      }
    };
  }, [user, fetchNotifications]);
  
  // Function to show desktop notification
  const showDesktopNotification = (notification) => {
    // Only proceed if the browser supports notifications
    if (!("Notification" in window)) {
      return;
    }
    
    // Check if permission is already granted
    if (Notification.permission === "granted") {
      createNotification(notification);
    } 
    // Otherwise, request permission
    else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          createNotification(notification);
        }
      });
    }
  };

  // Function to create and display the notification
  const createNotification = (notification) => {
    const title = notification.type === 'sms' 
      ? `New SMS from ${notification.sender || 'Unknown'}`
      : (notification.title || 'New Notification');
      
    const options = {
      body: notification.content,
      icon: '/logo192.png',
      badge: '/logo192.png'
    };
    
    const notif = new Notification(title, options);
    
    // Handle click on notification
    notif.onclick = function(event) {
      event.preventDefault();
      window.focus();
      handleNotificationClick(notification);
      notif.close();
    };
  };

  // Request notification permission
  const requestNotificationPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    fetchingError,
    showNotifications,
    setShowNotifications,
    notificationPanelRef,
    handleMarkAllRead,
    handleNotificationClick,
    requestNotificationPermission,
    fetchNotifications: () => fetchNotifications(false) // Expose non-silent refresh
  };
};

export default useNotifications; 
